import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Card, CardHead, CardBody } from "../../Components/CMI/primitives";
import { fmtEur, fmtPct, fmtN } from "../../Components/CMI/charts";
import { I } from "../../Components/CMI/icons";
import PageHead, { SectionHead } from "../../Layouts/CMI/PageHead";
import { getIvghHistorico, generarIvghSnapshot, cerrarIvghPeriodo, getIvghAuditLog } from "../../helpers/ivghApi";

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const SEMAFORO = {
  azul:  { bg: "#e8f4fd", color: "#0d6efd" },
  verde: { bg: "#e8f5e9", color: "#198754" },
  ambar: { bg: "#fff8e1", color: "#cc8800" },
  rojo:  { bg: "#fdecea", color: "#dc3545" },
};

const IvghHistorico = () => {
  document.title = "IVGH Histórico | CMI - Grupo Cucalón Estévez";
  const { year } = useSelector((s) => s.Filters);
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hist, log] = await Promise.all([
        getIvghHistorico(year),
        getIvghAuditLog(30),
      ]);
      setData(hist);
      setAudit(log);
    } catch (e) {
      console.error("[IVGH Hist]", e);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleSnapshot = async (mes) => {
    if (!window.confirm(`¿Generar snapshot de ${MESES[mes - 1]} ${year}?`)) return;
    try {
      const res = await generarIvghSnapshot(year, mes);
      setMsg(`Snapshot generado: ${res.gestores_guardados} gestores, ${res.equipos_guardados} equipos`);
      load();
    } catch (e) {
      setMsg("Error: " + (e.response?.data?.error || e.message));
    }
  };

  const handleCerrar = async (mes) => {
    if (!window.confirm(`¿Cerrar definitivamente ${MESES[mes - 1]} ${year}? No se podrá modificar.`)) return;
    try {
      await cerrarIvghPeriodo(year, mes);
      setMsg(`Periodo ${MESES[mes - 1]} ${year} cerrado`);
      load();
    } catch (e) {
      setMsg("Error: " + (e.response?.data?.error || e.message));
    }
  };

  const mesActual = new Date().getMonth() + 1;

  return (
    <div className="page-content">
      <PageHead icon={I.Calendar} title="IVGH — Histórico" sub="Snapshots mensuales y auditoría" />

      {msg && (
        <div style={{ padding: "8px 16px", marginBottom: 16, borderRadius: 8, background: "#e8f5e9", fontSize: 13 }}>
          {msg}
          <button onClick={() => setMsg(null)} style={{ float: "right", background: "none", border: "none", cursor: "pointer" }}>✕</button>
        </div>
      )}

      {loading && !data && (
        <div style={{ textAlign: "center", padding: 60, color: "var(--ce-fg-faint)" }}>Cargando…</div>
      )}

      {/* Controles de snapshot */}
      <SectionHead title="Generar / cerrar periodos" />
      <Card style={{ marginBottom: 24 }}>
        <CardBody>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Array.from({ length: mesActual }, (_, i) => {
              const m = i + 1;
              const snap = data?.meses?.find(s => s.mes === m);
              const cerrado = snap?.cerrado;
              return (
                <div key={m} style={{
                  border: "1px solid #ddd", borderRadius: 8, padding: "8px 12px",
                  background: cerrado ? "#e8f5e9" : snap ? "#fff8e1" : "#fff",
                  fontSize: 12, textAlign: "center", minWidth: 80,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{MESES[i]}</div>
                  {cerrado ? (
                    <span style={{ color: "var(--ce-success)", fontSize: 11 }}>✓ Cerrado</span>
                  ) : snap ? (
                    <div style={{ display: "flex", gap: 4, flexDirection: "column" }}>
                      <button className="btn btn-sm btn-outline-primary" style={{ fontSize: 10 }} onClick={() => handleSnapshot(m)}>
                        Regenerar
                      </button>
                      <button className="btn btn-sm btn-outline-success" style={{ fontSize: 10 }} onClick={() => handleCerrar(m)}>
                        Cerrar
                      </button>
                    </div>
                  ) : (
                    <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 10 }} onClick={() => handleSnapshot(m)}>
                      Snapshot
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Datos históricos por mes */}
      {data?.meses?.length > 0 && (
        <>
          <SectionHead title="Snapshots guardados" />
          {data.meses.map((mes) => (
            <Card key={mes.mes} style={{ marginBottom: 16 }}>
              <CardHead icon={I.Calendar} title={`${MESES[mes.mes - 1]} ${year}`} right={
                mes.cerrado
                  ? <span style={{ fontSize: 11, color: "var(--ce-success)", fontWeight: 600 }}>✓ CERRADO</span>
                  : <span style={{ fontSize: 11, color: "var(--ce-warning)" }}>Abierto</span>
              } />
              <CardBody>
                <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Gestor</th>
                      <th>Cat.</th>
                      <th>H. Reales</th>
                      <th>Importe</th>
                      <th>IVGH Real</th>
                      <th>IVGH Obj.</th>
                      <th>Cumpl.</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mes.gestores.map((g, j) => {
                      const sem = SEMAFORO[g.semaforo] || SEMAFORO.rojo;
                      return (
                        <tr key={j}>
                          <td style={{ textAlign: "left" }}>{g.nombre}</td>
                          <td>{g.categoria}</td>
                          <td>{fmtN(g.horas_trabajadas)}</td>
                          <td>{fmtEur(g.importe_gestionado)}</td>
                          <td style={{ fontWeight: 600 }}>{fmtEur(g.ivgh_real)}/h</td>
                          <td>{fmtEur(g.ivgh_objetivo)}/h</td>
                          <td>{fmtPct(g.cumplimiento)}</td>
                          <td>
                            <span style={{ padding: "1px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600, background: sem.bg, color: sem.color }}>
                              {g.semaforo}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          ))}
        </>
      )}

      {/* Log de auditoría */}
      {audit.length > 0 && (
        <>
          <SectionHead title="Registro de auditoría" />
          <Card>
            <CardBody>
              <table className="tbl" style={{ width: "100%", fontSize: 11 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Fecha</th>
                    <th style={{ textAlign: "left" }}>Acción</th>
                    <th style={{ textAlign: "left" }}>Entidad</th>
                    <th style={{ textAlign: "left" }}>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id}>
                      <td style={{ textAlign: "left", whiteSpace: "nowrap" }}>{new Date(a.created_at).toLocaleString("es-ES")}</td>
                      <td style={{ textAlign: "left" }}>{a.accion}</td>
                      <td style={{ textAlign: "left" }}>{a.entidad}{a.entidad_id ? ` #${a.entidad_id}` : ""}</td>
                      <td style={{ textAlign: "left", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={a.detalle}>{a.detalle}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default IvghHistorico;
